import { getImageSrc } from '@captaincause/lib/getImageSrc'
import { getTimeAgo } from '@captaincause/lib/getTimeAgo'
import youtubeLogo from '../assets/youtube.svg'
import { Button } from '../Button'
import { Html } from '../Html'
import { tm } from '../tm'
import type { Locales, ProjectUpdate } from '@captaincause/types'

export interface ProjectNewsCardProps {
  bodyClassName?: string
  className?: string
  imageClassName?: string
  locale: Locales
  news: ProjectUpdate
  onClick?: () => void
  showCreatedAt?: boolean
  showOrganizationName?: boolean
}

export function ProjectNewsCard({
  bodyClassName = '',
  className = '',
  imageClassName = '',
  locale,
  news,
  onClick = undefined,
  showCreatedAt = true,
  showOrganizationName = false,
}: ProjectNewsCardProps) {
  const youtube = getImageSrc(youtubeLogo)

  const image = news.youtube_id
    ? `https://img.youtube.com/vi/${news.youtube_id}/hqdefault.jpg`
    : news.image

  return (
    <Button
      key={news.id}
      variant="raw"
      onClick={onClick}
      className="flex-1 items-stretch rounded-md transition-transform hover:scale-[1.01] focus:outline-none focus-visible:scale-[1.01]"
      innerClassName={tm('flex flex-col w-full text-left', className)}
      targetBlank
      full
    >
      {showOrganizationName ? (
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-10 min-h-[40px] w-10 min-w-[40px] items-center justify-center overflow-hidden rounded-full border border-light-purple bg-white p-[1px]">
            <img
              src={news.project.organization.logo}
              alt={`${news.project.organization.name} logo`}
              className="h-full w-full rounded-full object-contain"
            />
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-medium leading-6">
              {news.project.organization.name}
            </span>
            <time
              className="text-[10px] leading-[8px] text-dark-gray lg:text-sm lg:leading-5 [&::first-letter]:capitalize"
              dateTime={news.created_at}
            >
              {getTimeAgo({
                dateString: news.created_at,
                locale,
              })}
            </time>
          </div>
        </div>
      ) : null}
      {image ? (
        <div
          className={tm(
            'mb-2 flex h-[140px] flex-col items-start',
            imageClassName,
          )}
        >
          <div className="relative mx-auto h-full w-full rounded-md">
            {news.youtube_id ? (
              <img
                src={youtube}
                alt=""
                className="absolute left-1/2 top-1/2 z-10 w-10 -translate-x-1/2 -translate-y-1/2"
              />
            ) : null}
            <img
              src={image}
              alt={`${news.name}`}
              className="absolute left-0 top-0 h-full w-full rounded-md object-cover"
              loading="lazy"
            />
          </div>
          {showCreatedAt && !showOrganizationName ? (
            <time className="mt-2 text-xs text-gray" dateTime={news.created_at}>
              {new Date(news.created_at).toLocaleDateString(locale)}
            </time>
          ) : null}
        </div>
      ) : null}

      <div className="mb-1 font-medium leading-relaxed">{news.name}</div>
      <Html
        className={tm(
          'line-clamp-4 text-xs leading-snug text-dark-gray [&_*]:inline',
          bodyClassName,
        )}
      >
        {news.body}
      </Html>
    </Button>
  )
}
